<template>
  <div>
    <table
      class="table table-striped mb-0 responsive-mobile-tablet-table p-lg-0 p-1"
    >
      <thead>
        <tr>
          <th v-if="selectable" class="pl-3 desktop-only">
            <!-- Hidden rather than inert on a page of folders: a box that does nothing when
               clicked reads as broken. -->
            <input
              v-if="selectableRows.length"
              type="checkbox"
              :checked="allOnPageSelected"
              aria-label="Select every document on this page"
              @change="toggleSelectAll"
            />
          </th>
          <th
            v-for="column in visibleColumns"
            :key="column.key"
            :class="column.class"
            :aria-sort="column.sortable ? sortState(column.key) : null"
          >
            <button
              v-if="column.sortable"
              type="button"
              class="sort-header-btn"
              @click="$emit('sort', column.key)"
            >
              {{ column.label }}
              <sort-caret
                :active="query.sort === column.key"
                :direction="query.direction"
              />
            </button>
            <template v-else>{{ column.label }}</template>
          </th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="d in rows"
          :key="d.kind === 'folder' ? 'f' + d.id : 'd' + d.id"
          :class="{ 'folder-row': isFolder(d), 'is-empty': isEmptyFolder(d) }"
          @click="isFolder(d) && $emit('open-folder', d)"
        >
          <td v-if="selectable" class="pl-3 desktop-only">
            <!-- Only documents are selectable: sharing a folder is its own action. -->
            <input
              v-if="!isFolder(d)"
              type="checkbox"
              :checked="selected.includes(d.id)"
              @change="toggleOne(d.id)"
            />
          </td>
          <!-- Driven by the same list as the headers, so the cells cannot drift out of step
             with them when a screen asks for a different set. -->
          <td
            v-for="column in visibleColumns"
            :key="column.key"
            :class="column.class"
          >
            <!-- Only folders have an audience, so on a document this label would head an empty
               cell — and a cell that is blank on desktop should be absent here, not labelled. -->
            <b
              v-if="column.key !== 'audience' || isFolder(d)"
              class="mobile-tablet-only mr-2"
              >{{ column.label }}:</b
            >

            <template v-if="column.key === 'name'">
              <!-- The icon belongs to the name, not to a column of its own: a column with no
                 header has nothing to label it with once the header row is hidden. One unit,
                 so a name long enough to wrap takes its icon down with it. -->
              <span class="node-name">
                <i
                  v-if="isFolder(d)"
                  class="node-icon fas fa-folder fa-lg folder-icon"
                  aria-hidden="true"
                ></i>
                <icon
                  v-else
                  :type="fileIcon(d)"
                  class="node-icon fa-lg text-muted m-0"
                />

                <span class="node-label">
                  <button
                    v-if="isFolder(d)"
                    type="button"
                    class="folder-name-btn"
                    @click.stop="$emit('open-folder', d)"
                  >
                    <span class="folder-name"
                      ><truncated-text :text="d.name" max="40ch"
                    /></span>
                  </button>
                  <document-name-link
                    v-else
                    :document="d"
                    :is-viewing="viewingId === d.id"
                    @view="viewDocument"
                  />
                  <!-- `@click.stop`: below 769px the tooltip's only trigger is a click, and the
                     row's own handler was swallowing it and opening the folder instead. -->
                  <span
                    v-if="isFolder(d) && d.is_shared_with_subject"
                    class="badge badge-light ml-2 shared-chip"
                    v-tooltip.top="sharedTooltip(d)"
                    @click.stop
                  >
                    {{ sharedLabel }}
                  </span>
                  <i
                    v-if="isFolder(d) && d.is_pinned"
                    class="fas fa-thumbtack ml-2 row-flag"
                    v-tooltip.top="'Pinned to the top of your list'"
                  ></i>
                </span>
              </span>
            </template>

            <template v-else-if="column.key === 'owner'">{{
              ownerLabel(d)
            }}</template>
            <!-- A folder reports what it holds where a document reports its size. -->
            <template v-else-if="column.key === 'count'">{{
              isFolder(d) ? itemCount(d) : d.size
            }}</template>
            <template v-else-if="column.key === 'added'">{{
              prettyDate(d.created_at)
            }}</template>
            <template v-else-if="column.key === 'last_activity'">{{
              prettyDate(d.last_activity || d.created_at)
            }}</template>
            <template v-else-if="column.key === 'audience'">
              <button
                v-if="isFolder(d)"
                class="btn btn-link btn-sm p-0 audience-link"
                @click.stop="$emit('show-audience', d)"
              >
                {{ d.audience_summary ? d.audience_summary.label : "" }}
              </button>
              <!-- Only folders have an audience; a blank cell reads as a bug. A card has no
                 column to fill, so it is dropped there. -->
              <span v-else class="desktop-only">—</span>
            </template>
          </td>

          <td class="td-actions">
            <div class="d-flex justify-content-center justify-content-xl-end">
              <folder-row-actions
                v-if="isFolder(d)"
                :folder="d"
                :subject-user-id="subjectUserId"
                @share="$emit('share-folder', $event)"
                @move="$emit('move-folder', $event)"
                @changed="$emit('changed')"
              />
              <document-row-actions
                v-else
                :document="d"
                :subject-user-id="subjectUserId"
                :can-delete="canDelete(d)"
                :can-share="canShareDocuments"
                :is-viewing="viewingId === d.id"
                @view="viewDocument"
                @share="$emit('share-document', $event)"
                @changed="$emit('changed')"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- One pair for the whole table; `viewingId` remembers which row asked. -->
    <file-viewer
      v-model="showFileViewer"
      :name="preview.name"
      :type="preview.type"
      :link="preview.link"
    />
    <video-viewer
      v-model="showVideoViewer"
      :videoName="preview.name"
      :videoLink="preview.link"
      :noDownload="true"
    />
  </div>
</template>

<script>
import { getDocumentIconName } from "@/utils/fileHelpers.js";
import helpers from "@/utils/helpers";
import { mapActions } from "vuex";
import { canDeleteDocument } from "./useDocumentsArea";

/**
 * Every column this table knows how to draw. `added` and `last_activity` are two different
 * questions — when it arrived, and when it last changed — so they are two columns, and the key
 * is the sort key sent to the API.
 */
const CATALOGUE = {
  name: { label: "Name", class: "", sortable: true },
  owner: { label: "Owner", class: "", sortable: false },
  count: { label: "Size", class: "text-right", sortable: true },
  added: { label: "Added", class: "", sortable: true },
  last_activity: { label: "Last activity", class: "", sortable: true },
  audience: { label: "Who can see it", class: "", sortable: false },
};

/** A folder list, or a folder's contents: both kinds of row, so both kinds' columns. */
const CONTAINER_COLUMNS = [
  "name",
  "owner",
  "count",
  "last_activity",
  "audience",
];

export default {
  name: "NodeTable",
  props: {
    rows: { type: Array, required: true },
    /** `{ sort, direction }` — whichever query object produced `rows`. */
    query: { type: Object, required: true },
    subjectUserId: { type: Number, default: null },
    /** Adds the leading checkbox column, for the screens that share in bulk. */
    selectable: { type: Boolean, default: false },
    selected: { type: Array, default: () => [] },
    /**
     * Which columns to draw, in order. "Shared with me" is a flat list of documents, so the
     * folder-only columns would be blank on every row of it.
     */
    columns: { type: Array, default: () => CONTAINER_COLUMNS },
    /** Whether a document row may offer Share — the folder's permission, decided by the caller. */
    canShareDocuments: { type: Boolean, default: false },
  },
  emits: [
    "sort",
    "changed",
    "update:selected",
    "open-folder",
    "share-folder",
    "share-document",
    "move-folder",
    "show-audience",
  ],
  data() {
    return {
      viewingId: null,
      showFileViewer: false,
      showVideoViewer: false,
      preview: { name: "", type: "", link: "" },
    };
  },
  computed: {
    /**
     * Always says who it reached, never just "Shared" — on someone else's tab that read as
     * something the viewer had done rather than how the folder got there.
     */
    sharedLabel() {
      const me = this.$store.state.auth.user && this.$store.state.auth.user.id;
      const ownTab =
        !this.subjectUserId || Number(this.subjectUserId) === Number(me);

      return ownTab ? "Shared with you" : "Shared with them";
    },

    visibleColumns() {
      return this.columns
        .filter((key) => CATALOGUE[key])
        .map((key) => ({ key, ...CATALOGUE[key] }));
    },

    /**
     * Folders are not selectable — sharing one is its own action — so they take no part in
     * "select all". Counting them made the button offer to share more documents than were
     * ticked, and put folder ids into a list the share sends as document ids.
     */
    selectableRows() {
      return this.rows.filter((row) => !this.isFolder(row));
    },

    allOnPageSelected() {
      return (
        this.selectableRows.length > 0 &&
        this.selectableRows.every((d) => this.selected.includes(d.id))
      );
    },
  },
  methods: {
    ...mapActions({ fetchViewLink: "folders/view" }),

    viewDocument(doc) {
      if (this.viewingId) {
        return;
      }
      this.viewingId = doc.id;

      this.fetchViewLink({ ...doc, owner_id: this.subjectUserId })
        .then((link) => {
          if (!link) {
            return;
          }
          this.preview = {
            name: doc.original_file_name,
            type: doc.type,
            link,
          };
          if (doc.type === "video") {
            this.showVideoViewer = true;
          } else {
            this.showFileViewer = true;
          }
        })
        .finally(() => {
          this.viewingId = null;
        });
    },

    /** `aria-sort` for one column header — screen readers announce the current sort state. */
    sortState(column) {
      if (this.query.sort !== column) {
        return "none";
      }

      return this.query.direction === "asc" ? "ascending" : "descending";
    },

    /** A container listing carries both kinds; the Shared-with-me list carries documents only. */
    isFolder(row) {
      return row.kind === "folder";
    },

    /** Who put it here, since the chip only says that it was shared. */
    sharedTooltip(folder) {
      const owner = folder.owner && folder.owner.name;

      return owner ? `Shared by ${owner}` : "Shared onto this tab";
    },

    ownerLabel(row) {
      if (!row.owner || !row.owner.name) {
        return "—";
      }

      return row.owner.is_you ? "You" : row.owner.name;
    },

    /**
     * Nothing directly inside means nothing anywhere below either, since a subtree can only
     * hang off a subfolder.
     */
    isEmptyFolder(row) {
      return this.isFolder(row) && !row.documents_count && !row.subfolder_count;
    },

    /** Files and subfolders alike — a folder holding one of each holds two things, not one. */
    itemCount(folder) {
      const count =
        (folder.documents_count || 0) + (folder.subfolder_count || 0);

      return count === 1 ? "1 item" : count + " items";
    },

    // Per document, not per folder: a client's file in a staff folder is still the client's.
    canDelete(doc) {
      return canDeleteDocument(doc);
    },

    fileIcon(doc) {
      return getDocumentIconName(doc);
    },

    prettyDate(date) {
      return date ? helpers.prettyDate(date) : "—";
    },

    toggleSelectAll(event) {
      this.$emit(
        "update:selected",
        event.target.checked ? this.selectableRows.map((d) => d.id) : []
      );
    },

    toggleOne(id) {
      this.$emit(
        "update:selected",
        this.selected.includes(id)
          ? this.selected.filter((x) => x !== id)
          : [...this.selected, id]
      );
    },
  },
};
</script>

<style scoped lang="scss">
.folder-row {
  cursor: pointer;
}

/* Only where there is a pointer to hover with; below this the actions are a card footer.
   `opacity`, not `visibility` — `visibility: hidden` drops them from the Tab order too. */
@media (min-width: 1025px) {
  .folder-row :deep(.row-actions) {
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease-in-out;
  }

  .folder-row:hover :deep(.row-actions),
  .folder-row:focus-within :deep(.row-actions) {
    opacity: 1;
    pointer-events: auto;
  }

  .folder-row:hover :deep(.row-chevron),
  .folder-row:focus-within :deep(.row-chevron) {
    opacity: 0;
  }
}

@media (max-width: 1024px) {
  :deep(.row-chevron) {
    display: none;
  }
}

/* Defined here, not in either child component, because both kinds of row draw it. */
:deep(.action-circle) {
  /* Flex-centred: on a shared baseline, four icon glyphs of four heights sat at four heights. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 0;
  background: #ffffff;
  color: var(--main-text-color);
  margin: 0.2rem 0.175rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  font-size: 0.8rem;
  line-height: 1;
  transition: transform 0.1s ease-in-out;
}

:deep(.action-circle:hover:not(:disabled)) {
  transform: scale(1.08);
}

:deep(.action-circle:disabled) {
  opacity: 0.45;
  box-shadow: none;
  cursor: not-allowed;
}

:deep(.action-circle-danger) {
  background: #dc3545;
  color: #ffffff;
}

/* A toggle has to show which way it is set; the tooltip alone needed a hover to find out. */
:deep(.action-circle-active) {
  background: var(--theme-color);
  color: var(--btn-text-color, #ffffff);
}

/* A <button> is an atomic inline box — a long name moves whole, so the icon shares its unit
   rather than being stranded on the line above. */
.node-name {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
}

.node-icon {
  flex: 0 0 auto;
  margin-right: 0.5rem;
  /* An absolute line box equal to the text's. A unitless 1.5 multiplies the icon's OWN size,
     and `fa-lg` is 1.33rem, which left the glyph sitting 3px low against the name. */
  line-height: 1.5rem;
}

/* Without this the name refuses to shrink and overflows the unit. */
.node-label {
  min-width: 0;
}

.folder-icon {
  color: #f5c344;
}

/* Greyed rather than hidden — the count already says nought, this makes it legible at a glance. */
.is-empty .folder-icon {
  opacity: 0.55;
}

.is-empty td {
  color: var(--main-text-color);
  opacity: 0.55;
}

.folder-name-btn {
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  max-width: 100%;
  min-width: 0;
}

.folder-name-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.folder-row .folder-name {
  font-weight: 600;
}

.folder-row:hover .folder-name {
  text-decoration: underline;
}

/* Left as the link colour `.btn-link` gives it: in this app a clickable value inside a cell is
   a plain link, and the colour is the only thing that says so before you hover. */
.audience-link:hover {
  text-decoration: underline;
}

.shared-chip {
  font-weight: 500;
  vertical-align: middle;
}

.row-flag {
  color: var(--main-text-color);
  opacity: 0.6;
  font-size: 0.8rem;
}

/* A real <button> styled back down to header text: focusable and Enter/Space-activatable. */
.sort-header-btn {
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.sort-header-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
